import axios from 'axios'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Principal } from '../types'
import { getMe } from '../lib/api'
import { setBusinessTimeZone } from '../lib/businessTime'
import { CLIENT_PLACE_HANDOFF_KEY } from '../lib/sessionKeys'

interface AuthState {
  principal: Principal | null
  token: string | null
  isAnalyst: boolean
  isAdmin: boolean
  // Routing hint only — it decides which dashboard to send someone to, not
  // what they may see. Every /org request is re-authorised server-side by
  // get_current_org_admin, which also requires a business tenant.
  isOrgAdmin: boolean
  isLoading: boolean
  login: (token: string) => Promise<void>
  logout: () => void
  checkAuth: () => boolean
  restoreSession: () => Promise<void>
}

function deriveRoles(principal: Principal) {
  return {
    isAnalyst: principal.role === 'analyst' || principal.role === 'admin',
    isAdmin: principal.role === 'admin',
    isOrgAdmin:
      principal.org_role === 'owner' || principal.org_role === 'admin',
  }
}

function decodeTokenPayload(token: string): { sub: string; role: string; tenant_id: string; email: string } | null {
  try {
    const base64 = token.split('.')[1]
    const json = atob(base64)
    return JSON.parse(json)
  } catch {
    return null
  }
}

// DW-CLIENT-PLACE-CHECKAUTH-SEES-BEARER: mirrors lib/api.ts's
// getStoredBearerToken (dw_token, else the dw-auth-storage persist
// snapshot) without importing it — authStore.test.ts mocks '../lib/api' as
// `{ getMe }` only, so pulling another export from that module here would
// resolve to undefined under the mock.
function readStoredBearerToken(): string | null {
  const direct = localStorage.getItem('dw_token')
  if (direct && direct.trim()) return direct
  const raw = localStorage.getItem('dw-auth-storage')
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as { state?: { token?: unknown } }
    const token = parsed?.state?.token
    return typeof token === 'string' && token.trim() ? token : null
  } catch {
    return null
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      principal: null,
      token: null,
      isAnalyst: false,
      isAdmin: false,
      isOrgAdmin: false,
      isLoading: false,

      login: async (token: string) => {
        localStorage.setItem('dw_token', token)
        set({ token, isLoading: true })
        try {
          const principal = await getMe()
          set({ principal, ...deriveRoles(principal), isLoading: false })
        } catch (err) {
          // DW-CLIENT-PLACE-USES-LOGIN-JWT: only a genuine 401 means the
          // token itself is bad. A network failure or 5xx says nothing about
          // the token's validity — wiping dw_token here used to erase a JWT
          // that placeTripwire (or a caller who already used it directly)
          // still needed. Keep the token and let the caller retry getMe.
          if (axios.isAxiosError(err) && err.response?.status === 401) {
            // DW-CLIENT-PLACE-TRY-AGAIN-KEEPS-BEARER: a tripwire place is in
            // flight for this sign-in (completeLogin wrote dw_token, maybe
            // already placed with it directly, and set the handoff marker
            // before calling login()). Wiping dw_token on a getMe 401 here
            // would erase the Bearer the error-sheet's "Try again" still
            // needs to retry the place — leave the JWT alone; only the
            // principal stays unset.
            if (sessionStorage.getItem(CLIENT_PLACE_HANDOFF_KEY)) {
              set({ isLoading: false })
            } else {
              localStorage.removeItem('dw_token')
              set({
                token: null,
                principal: null,
                isAnalyst: false,
                isAdmin: false,
                isOrgAdmin: false,
                isLoading: false,
              })
            }
          } else {
            set({ isLoading: false })
          }
        }
      },

      logout: () => {
        localStorage.removeItem('dw_token')
        set({
          principal: null,
          token: null,
          isAnalyst: false,
          isAdmin: false,
          isOrgAdmin: false,
        })
        window.location.href = '/'
      },

      checkAuth: () => {
        const token = get().token
        if (token && token.trim()) return true
        // DW-CLIENT-PLACE-CHECKAUTH-SEES-BEARER: a JWT place (completeLogin)
        // writes dw_token — and a rehydrated persist snapshot can carry a
        // token — before zustand's own `token` field is ever set. Without
        // this, ProtectedRoute keeps rendering LoginPage over a session that
        // already has a valid Bearer, and the tripwire decoy never paints.
        return !!readStoredBearerToken()
      },

      restoreSession: async () => {
        let token = get().token
        if (!token || !token.trim()) {
          // Adopt a Bearer that already exists in storage (dw_token from a
          // JWT place, or the dw-auth-storage persist snapshot) so restore
          // can proceed even though zustand's in-memory `token` is still
          // null on this render.
          const stored = readStoredBearerToken()
          if (!stored) return
          token = stored
          set({ token })
        }
        // The persist middleware only rehydrates `token` in-memory; the raw
        // dw_token localStorage key (read by the axios interceptor and by
        // placeTripwire, which cannot use the apiClient instance) can lag
        // behind it — e.g. a fresh tab restoring from dw-auth-storage before
        // dw_token is ever (re)written. Keep them in sync on every restore so
        // a client landing on /client can actually place a tripwire.
        if (!localStorage.getItem('dw_token')) {
          localStorage.setItem('dw_token', token)
        }
        // DW-CLIENT-PLACE-RESTORE-STAYS-ON-CLIENT: logout() redirects to '/'
        // and wipes dw_token — right for an explicit sign-out, wrong here.
        // A getMe miss on restore (network blip, 5xx, or an expired token
        // that hasn't reached a 401 yet) must not bounce a client off /client
        // and erase the Bearer the tripwire still needs. Only a confirmed-bad
        // token (invalid payload or a genuine 401) gets wiped, and even then
        // without redirecting — the caller stays wherever it already is.
        const payload = decodeTokenPayload(token)
        if (!payload) {
          localStorage.removeItem('dw_token')
          set({
            token: null,
            principal: null,
            isAnalyst: false,
            isAdmin: false,
            isOrgAdmin: false,
            isLoading: false,
          })
          return
        }
        set({ isLoading: true })
        try {
          const principal = await getMe()
          set({ principal, ...deriveRoles(principal), isLoading: false })
        } catch (err) {
          if (axios.isAxiosError(err) && err.response?.status === 401) {
            // DW-CLIENT-PLACE-TRY-AGAIN-KEEPS-BEARER: see login()'s matching
            // comment — a place in flight for this sign-in must keep its
            // dw_token through a getMe 401 so the error-sheet's "Try again"
            // still has a Bearer to retry the place with.
            if (sessionStorage.getItem(CLIENT_PLACE_HANDOFF_KEY)) {
              set({ isLoading: false })
            } else {
              localStorage.removeItem('dw_token')
              set({
                token: null,
                principal: null,
                isAnalyst: false,
                isAdmin: false,
                isOrgAdmin: false,
                isLoading: false,
              })
            }
          } else {
            set({ isLoading: false })
          }
        }
      },
    }),
    {
      name: 'dw-auth-storage',
      partialize: (state) => ({ token: state.token }),
    },
  ),
)

// BRIEF-E5d: the customer pages show times on the business's own clock, which
// /auth/me gives with the principal. Rehydrated and fresh principals both land here.
function syncBusinessClock(principal: Principal | null) {
  setBusinessTimeZone(principal?.business_time_zone)
}
syncBusinessClock(useAuthStore.getState().principal)
useAuthStore.subscribe((s) => syncBusinessClock(s.principal))
